// src/pages/Login.jsx
import { useState } from "react";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { ref, get, push, update } from "firebase/database";
import { auth, db } from "../firebase";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  LinearProgress,
  Stack,
  IconButton,
  Link,
  Avatar,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

/* ---------- config ---------- */
const ALLOWED_ROLES = ["MAIN_ADMIN", "ADMIN", "HR"];
const FUNCTIONS_BASE =
  import.meta.env.VITE_FUNCTIONS_BASE ||
  "https://us-central1-tdo-emp.cloudfunctions.net";

/* ---------- helpers ---------- */

async function callFn(path, idToken, body) {
  const res = await fetch(`${FUNCTIONS_BASE}/${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body || {}),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || "internal";
    throw new Error(msg);
  }
  return data;
}

async function logAdminLogin(user, role) {
  const uid = user.uid;
  const nowIso = new Date().toISOString();
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const tz =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "";

  let geo = null;
  try {
    const res = await fetch("https://ipapi.co/json/");
    if (res.ok) geo = await res.json();
  } catch (e) {
    console.warn("Geo lookup failed", e);
  }

  // write a short log entry (under adminLoginLogs)
  const logRef = ref(db, `/adminLoginLogs/${uid}`);
  try {
    await push(logRef, {
      at: nowIso,
      role: role || null,
      ua,
      tz,
      approx: geo
        ? {
            ip: geo.ip,
            city: geo.city,
            region: geo.region,
            country: geo.country_name,
            lat: geo.latitude,
            lon: geo.longitude,
            org: geo.org,
          }
        : null,
    });
  } catch (e) {
    console.warn("Failed to write login log", e);
  }

  // update admin metadata under /admins/{uid}
  try {
    const adminRef = ref(db, `/admins/${uid}`);
    await update(adminRef, {
      lastLoginAt: nowIso,
      lastLoginIp: geo?.ip || null,
      lastLoginCity: geo?.city || null,
      lastLoginCountry: geo?.country_name || null,
    });
  } catch (e) {
    console.warn("Failed to update admin metadata", e);
  }
}

/* sanitize Firebase errors so no raw Firebase strings leak to admins */
function sanitizeAuthError(ex) {
  const raw = ex && ex.message ? String(ex.message) : String(ex || "");
  const lower = raw.toLowerCase();

  // Map some common auth cases to friendly text
  if (/wrong-password/i.test(lower) || /auth\/wrong-password/i.test(lower)) {
    return "Admin login$ Incorrect email or password.";
  }
  if (/user-not-found/i.test(lower) || /auth\/user-not-found/i.test(lower)) {
    return "Admin login$ No account found for this email.";
  }
  if (/invalid-email/i.test(lower) || /auth\/invalid-email/i.test(lower)) {
    return "Admin login$ Invalid email address.";
  }
  if (/too-many-requests/i.test(lower) || /auth\/too-many-requests/i.test(lower)) {
    return "Admin login$ Too many attempts. Try again later.";
  }
  if (/user-disabled/i.test(lower) || /auth\/user-disabled/i.test(lower)) {
    return "Admin login$ This account has been disabled. Contact an administrator.";
  }
  // generic firebase/auth style messages (catch-all)
  if (/firebase/i.test(lower) || /auth\//i.test(lower) || /credential/i.test(lower)) {
    return "Admin login$ Authentication failed. Please check your credentials and try again.";
  }

  // otherwise return cleaned message but prefix for consistency
  return `Admin login$ ${raw}`;
}

/* ---------- component ---------- */

export default function Login() {
  const nav = useNavigate();
  const loc = useLocation();

  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // password visibility
  const [showPw, setShowPw] = useState(false);

  // step: "PASSWORD" -> "TOTP"
  const [step, setStep] = useState("PASSWORD");
  const [approvedRole, setApprovedRole] = useState(null);

  const redirectTarget = loc.state?.from?.pathname || "/dashboard";

  async function handlePasswordLogin(e) {
    e.preventDefault();
    if (loading) return;

    setErr("");
    setLoading(true);

    try {
      // 1️⃣ Sign in with Firebase
      const cred = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        pw.trim()
      );
      const user = cred.user;
      const uid = user.uid;

      // 2️⃣ Admin record check (now stored under /admins)
      const snap = await get(ref(db, `/admins/${uid}`));
      const adminRec = snap.val() || null;
      const role = adminRec?.role;
      const active = adminRec?.active !== false;
      const roleNorm = String(role || "").toUpperCase();

      if (!role || !ALLOWED_ROLES.includes(roleNorm) || !active) {
        // Clear auth session and show message
        await signOut(auth);
        if (!role) {
          setErr("Admin login$% Access denied. No admin record found for this user.");
        } else if (!active) {
          setErr("Admin login$% Access denied. This admin account is disabled.");
        } else {
          setErr(`Admin login$% Role "${role}" is not authorized for the admin panel.`);
        }
        return;
      }

      setApprovedRole(roleNorm);

      // 3️⃣ Check TOTP status for this admin
      const idToken = await user.getIdToken(/* forceRefresh = */ true);
      let enabled = false;
      try {
        const status = await callFn("getAdminTotpStatus", idToken, {});
        enabled = !!status.enabled;
      } catch (e) {
        // If the function call fails, log & continue: we'll fall back to TOTP step to be safe.
        console.warn("getAdminTotpStatus failed", e);
      }

      if (!enabled) {
        // First time → force setup page
        nav("/setup-mfa", {
          replace: true,
          state: { from: { pathname: redirectTarget } },
        });
      } else {
        // TOTP required → show OTP step
        setStep("TOTP");
        setOtp("");
      }
    } catch (ex) {
      console.error("Login error:", ex);
      setErr(sanitizeAuthError(ex));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyTotp(e) {
    e.preventDefault();
    if (loading || !otp) return;

    setErr("");
    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Session expired. Please sign in again.");

      // ensure fresh token
      const idToken = await user.getIdToken(/* forceRefresh = */ true);
      const data = await callFn("verifyAdminTotpLogin", idToken, {
        code: otp.trim(),
      });

      if (!data.ok) throw new Error("Invalid code");

      // Only here do we consider the login fully successful
      await logAdminLogin(user, approvedRole || null);

      nav(redirectTarget, { replace: true });
    } catch (ex) {
      console.error("verifyAdminTotpLogin error:", ex);
      setErr(sanitizeAuthError(ex));
    } finally {
      setLoading(false);
    }
  }

  const showPasswordStep = step === "PASSWORD";
  const showOtpStep = step === "TOTP";

  // logo: try /logo.png (deploy to hosting) else fallback to icon
  const logoSrc = "https://thinkersclub.online/assets/logo.png";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "linear-gradient(180deg, #eef2ff 0%, #ffffff 60%)",
        px: 2,
        py: 6,
      }}
    >
      <Card sx={{ width: 420, maxWidth: "100%", boxShadow: 12, borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Avatar
              src={logoSrc}
              alt="ThinkersClub"
              sx={{ width: 72, height: 72, bgcolor: "transparent" }}
            >
              <AdminPanelSettingsIcon sx={{ fontSize: 44 }} />
            </Avatar>
          </Box>

          <Typography variant="h5" align="center" sx={{ mb: 1, fontWeight: 700 }}>
            Admin Sign In
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Secure access to the ThinkersClub admin panel
          </Typography>

          {err && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          )}

          {showPasswordStep && (
            <form onSubmit={handlePasswordLogin}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  placeholder="you@company.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Password"
                  type={showPw ? "text" : "password"}
                  fullWidth
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowPw((s) => !s)}
                          aria-label={showPw ? "Hide password" : "Show password"}
                          size="small"
                        >
                          {showPw ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  type="submit"
                  disabled={loading}
                  sx={{ py: 1.5, fontWeight: 600 }}
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="caption" color="text.secondary">Need help? contact IT</Typography>
                </Box>

                {loading && <LinearProgress />}
              </Stack>
            </form>
          )}

          {showOtpStep && (
            <form onSubmit={handleVerifyTotp}>
              <Stack spacing={2}>
                <Typography variant="body1" align="center" sx={{ mb: 1 }}>
                  Enter the <strong>6-digit code</strong> from your authenticator app.
                </Typography>
                <TextField
                  label="Authenticator code"
                  fullWidth
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 6 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  type="submit"
                  disabled={loading || !otp}
                  sx={{ py: 1.5, fontWeight: 600 }}
                >
                  {loading ? "Verifying…" : "Verify & continue"}
                </Button>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Link component="button" variant="body2" onClick={() => { setStep("PASSWORD"); setOtp(""); }}>
                    Back to password
                  </Link>
                </Box>
                {loading && <LinearProgress />}
              </Stack>
            </form>
          )}
        </CardContent>

        <Box sx={{ px: 3, pb: 2 }}>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            By signing in you agree to internal usage policies. If you have trouble, contact the system administrator.
          </Typography>
        </Box>
      </Card>
    </Box>
  );
}
